/**
 * AdvisoryPage · compliance-safe replacement for the old paid "Recovery" wizard.
 *
 * There is NO paid service catalog, NO account-specific "recovery for IG / Gmail
 * / WhatsApp / TikTok" targeting, NO Cashfree payment flow. This page is a free
 * cyber-defense advisory contact form — visitors submit a general question, we
 * email them back with educational guidance and links to the platform's own
 * official recovery / appeal channels.
 *
 * All the earlier RecoveryPage / RecoveryWizard code has been retired; existing
 * `/recovery` links throughout the site are transparently routed here.
 */
import React, { useState } from 'react';
import { toast } from 'sonner';
import {
  ShieldCheck, Send, Loader2, CheckCircle2, MessageCircle, Mail,
  BookOpen, Terminal, ExternalLink, Lock, HeartHandshake,
} from 'lucide-react';
import { api } from '../lib/api';
import { useSiteConfig } from '../contexts/SiteConfigContext';
import RelatedServicesStrip from '../components/RelatedServicesStrip';

// Small stat / promise chip.
const Chip = ({ Icon, children }) => (
  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-[var(--eh-border)] text-[10px] eh-mono opacity-90">
    <Icon size={10} /> {children}
  </span>
);

const AdvisoryPage = () => {
  const { config } = useSiteConfig();
  const site = config.site || {};
  const telegramHref = site.telegram || 'https://t.me/errorhacker';
  const emailHref = site.email ? `mailto:${site.email}` : 'mailto:root@errorhacker.site';

  const [form, setForm] = useState({ name: '', email: '', topic: '', message: '' });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const patch = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email.trim() || !form.message.trim()) {
      toast.error('Email and message are required'); return;
    }
    setBusy(true);
    try {
      await api.advisoryContact({
        name: form.name.trim(),
        email: form.email.trim(),
        topic: form.topic.trim() || 'General question',
        message: form.message.trim(),
      });
      setDone(true);
      toast.success('Message sent. We\'ll reply within 24h.');
    } catch (err) {
      toast.error(err?.message || 'Send failed. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen pt-20 pb-16 px-4 sm:px-6" data-testid="advisory-page">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <header className="mb-8">
          <div className="eh-mono text-[10px] tracking-widest opacity-70 mb-2">// CYBER-DEFENSE ADVISORY</div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-black mb-3" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Talk to a <span className="eh-neon">cyber-defense analyst</span>.
          </h1>
          <p className="max-w-2xl text-sm sm:text-base opacity-80 leading-relaxed">
            We provide <b>free general guidance</b> and educational material for anyone
            who has been hacked, phished, doxed, or is worried about their digital
            security. This page is <b>not</b> a paid account-recovery service and we
            never log in to any account on your behalf. For platform-specific issues
            we will point you at the platform&apos;s <i>official</i> recovery channel
            and share our best-practice checklist.
          </p>
          <div className="flex flex-wrap gap-2 mt-4">
            <Chip Icon={Lock}>We never log in for you</Chip>
            <Chip Icon={ShieldCheck}>Free general guidance</Chip>
            <Chip Icon={HeartHandshake}>24h reply · analyst-signed</Chip>
          </div>
        </header>

        {/* Two columns: quick contact  ·  contact form */}
        <div className="grid lg:grid-cols-3 gap-4 mb-6">
          {/* Quick-contact card */}
          <div className="eh-panel p-4">
            <div className="eh-kicker mb-3">// FASTER · JUST DM</div>
            <a
              href={telegramHref} target="_blank" rel="noreferrer"
              data-testid="advisory-telegram-btn"
              className="flex items-center gap-2 mb-2 px-3 py-2.5 rounded-md border border-[var(--eh-border)] hover:border-[var(--eh-green)] transition-colors group"
            >
              <MessageCircle size={16} className="text-[#4de0ff]" />
              <div className="flex-1 min-w-0">
                <div className="text-xs eh-mono font-bold">Telegram</div>
                <div className="text-[10px] opacity-60 truncate">{telegramHref.replace('https://', '')}</div>
              </div>
              <ExternalLink size={12} className="opacity-40 group-hover:opacity-80" />
            </a>
            <a
              href={emailHref}
              data-testid="advisory-email-btn"
              className="flex items-center gap-2 px-3 py-2.5 rounded-md border border-[var(--eh-border)] hover:border-[var(--eh-green)] transition-colors group"
            >
              <Mail size={16} className="text-[#ffd34d]" />
              <div className="flex-1 min-w-0">
                <div className="text-xs eh-mono font-bold">Email</div>
                <div className="text-[10px] opacity-60 truncate">{site.email || 'root@errorhacker.site'}</div>
              </div>
              <ExternalLink size={12} className="opacity-40 group-hover:opacity-80" />
            </a>

            <div className="mt-4 pt-4 border-t border-[var(--eh-border)]">
              <div className="eh-kicker mb-2">// SELF-SERVE</div>
              <a href="/tools" className="flex items-center gap-2 px-3 py-2 rounded-md border border-transparent hover:border-[var(--eh-border)] transition-colors">
                <Terminal size={14} className="text-[#4de0ff]" />
                <div className="text-xs eh-mono">Free AI security tools</div>
              </a>
              <a href="/books" className="flex items-center gap-2 px-3 py-2 rounded-md border border-transparent hover:border-[var(--eh-border)] transition-colors">
                <BookOpen size={14} className="text-[#ff2d92]" />
                <div className="text-xs eh-mono">Educational library</div>
              </a>
              <a href="/ethics" className="flex items-center gap-2 px-3 py-2 rounded-md border border-transparent hover:border-[var(--eh-border)] transition-colors">
                <ShieldCheck size={14} className="text-[var(--eh-green)]" />
                <div className="text-xs eh-mono">Our ethics charter</div>
              </a>
            </div>
          </div>

          {/* Contact form */}
          <div className="lg:col-span-2 eh-panel p-4 sm:p-5">
            {done ? (
              <div className="text-center py-10" data-testid="advisory-success">
                <CheckCircle2 size={44} className="mx-auto mb-3 text-[var(--eh-green)]" />
                <div className="text-xl font-bold mb-2">Message received.</div>
                <p className="text-sm opacity-80 max-w-md mx-auto">
                  A senior analyst will reply within 24h with general guidance and links
                  to the appropriate official channels. If it&apos;s urgent, drop us a
                  message on Telegram above.
                </p>
                <button
                  onClick={() => { setDone(false); setForm({ name: '', email: '', topic: '', message: '' }); }}
                  className="mt-6 eh-btn-ghost text-xs"
                  data-testid="advisory-send-another"
                >
                  Send another message
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4" data-testid="advisory-form">
                <div>
                  <div className="eh-kicker mb-2">// STEP 1 — TELL US ABOUT YOU</div>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <label className="block">
                      <div className="text-[10px] eh-mono opacity-70 mb-1">NAME (OPTIONAL)</div>
                      <input
                        type="text" value={form.name} onChange={patch('name')}
                        data-testid="advisory-name"
                        placeholder="your name or callsign"
                        className="w-full eh-input"
                      />
                    </label>
                    <label className="block">
                      <div className="text-[10px] eh-mono opacity-70 mb-1">EMAIL *</div>
                      <input
                        type="email" required value={form.email} onChange={patch('email')}
                        data-testid="advisory-email"
                        placeholder="you@example.com"
                        className="w-full eh-input"
                      />
                    </label>
                  </div>
                </div>
                <div>
                  <div className="eh-kicker mb-2">// STEP 2 — WHAT&apos;S GOING ON?</div>
                  <label className="block mb-3">
                    <div className="text-[10px] eh-mono opacity-70 mb-1">TOPIC (OPTIONAL)</div>
                    <input
                      type="text" value={form.topic} onChange={patch('topic')}
                      data-testid="advisory-topic"
                      placeholder="e.g. suspicious email · phishing · lost account · privacy"
                      className="w-full eh-input"
                    />
                  </label>
                  <label className="block">
                    <div className="text-[10px] eh-mono opacity-70 mb-1">MESSAGE *</div>
                    <textarea
                      required value={form.message} onChange={patch('message')} rows={6}
                      data-testid="advisory-message"
                      placeholder="Describe the situation in plain English. Include timing, platform (if any), and what you've already tried. Do NOT include passwords, OTPs or full credit-card numbers."
                      className="w-full eh-input resize-y"
                    />
                  </label>
                </div>
                <div className="flex items-center justify-between flex-wrap gap-3 pt-2">
                  <div className="text-[10px] eh-mono opacity-60 max-w-md">
                    Submitting this form does not create a paid engagement. We reply with
                    free general guidance and, if the case exceeds free advice, we may
                    suggest an authorised audit engagement or a book.
                  </div>
                  <button
                    type="submit" disabled={busy}
                    data-testid="advisory-submit"
                    className="eh-btn-primary text-xs flex items-center gap-2 disabled:opacity-60"
                  >
                    {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                    SEND MESSAGE
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>

        {/* Foot-note & cross-service strip */}
        <div className="text-[11px] eh-mono opacity-60 mb-6 leading-relaxed">
          <b>What we do:</b> free educational guidance · point you at the official
          platform recovery channel · authorised web / cloud security audits (paid,
          scoped, contracted) · security education (books, memberships, mentorship).
          <br />
          <b>What we do not do:</b> log in to any account on your behalf · bypass any
          platform&apos;s authentication · sell followers / likes / views · guarantee
          third-party platform outcomes.
        </div>
        <RelatedServicesStrip />
      </div>
    </div>
  );
};

export default AdvisoryPage;
